import { ChevronRight } from "@app/ui";
import { useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import type { Topic } from "@app/core";
import { useSettingsStore, useThemeStore, useTopicFilterStore, useTopicsStore } from "../../stores/index.js";
import { resolvedTopicColor } from "../entity-colors.js";
import { orderByIds } from "../../lib/apply-order.js";
import { useI18n } from "../../lib/useI18n.js";
import { useDragClickGuard } from "../../lib/useDragClickGuard.js";
import { Reorder, useDragControls } from "../../lib/motion/index.js";
import { ModeToggle } from "../sidebar/ModeToggle.js";
import { SidebarSection } from "../sidebar/SidebarSection.js";
import { useSidebarReorderProps } from "../sidebar/useSidebarReorderProps.js";

/** Nested topic list for the sidebar that doubles as a view filter. The section
 *  collapses (persisted) via SidebarSection; inside, parents collapse/expand
 *  (local) and clicking a topic toggles it in the topic filter. **All levels**
 *  are drag-to-reorder: roots follow `topicOrder`, children follow
 *  `topicChildOrder[parentId]` (both persisted). An Any/All toggle picks the
 *  match mode. Each Reorder.Item carries explicit z-index so dragging upward
 *  doesn't let the displaced item render on top of the dragged one. */
export function TopicTree({ grip, onTitlePointerDown }: { grip?: ReactNode; onTitlePointerDown?: (e: ReactPointerEvent) => void } = {}) {
  const { t } = useI18n();
  const topics = useTopicsStore((s) => s.topics);
  const themeConfig = useThemeStore((s) => s.config);
  const { selected, mode, toggle, clear, setMode } = useTopicFilterStore();
  const sectionCollapsed = useSettingsStore((s) => s.sidebarCollapsed.topics);
  const setSidebarCollapsed = useSettingsStore((s) => s.setSidebarCollapsed);
  const topicOrder = useSettingsStore((s) => s.topicOrder);
  const setTopicOrder = useSettingsStore((s) => s.setTopicOrder);
  const topicChildOrder = useSettingsStore((s) => s.topicChildOrder);
  const setTopicChildOrder = useSettingsStore((s) => s.setTopicChildOrder);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const guard = useDragClickGuard();
  const reorderProps = useSidebarReorderProps();

  if (topics.length === 0) return null;

  const ids = new Set(topics.map((t) => t.id));
  const childrenOf = new Map<string | null, Topic[]>();
  for (const t of topics) {
    const key = t.parent_id && ids.has(t.parent_id) ? t.parent_id : null;
    const list = childrenOf.get(key) ?? [];
    list.push(t);
    childrenOf.set(key, list);
  }
  // Children default to alpha order; roots follow the user's drag order.
  for (const [key, list] of childrenOf) if (key !== null) list.sort((a, b) => a.name.localeCompare(b.name));
  const roots = orderByIds(childrenOf.get(null) ?? [], topicOrder, (t) => t.id);

  function toggleCollapse(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // Renders a Reorder.Group for one level of the tree. Recurses for children.
  function renderLevel(levelTopics: Topic[], depth: number, parentId: string | null): ReactNode {
    if (levelTopics.length === 0) return null;
    const ordered = parentId === null
      ? roots
      : orderByIds(levelTopics, topicChildOrder[parentId] ?? [], (t) => t.id);
    return (
      <Reorder.Group
        as="div"
        axis="y"
        values={ordered.map((t) => t.id)}
        onReorder={(ids) => {
          if (parentId === null) void setTopicOrder(ids as string[]);
          else void setTopicChildOrder(parentId, ids as string[]);
        }}
      >
        {ordered.map((topic) => {
          const kids = childrenOf.get(topic.id) ?? [];
          const isCollapsed = collapsed.has(topic.id);
          return (
            <TopicItem
              key={topic.id}
              topic={topic}
              depth={depth}
              active={selected.includes(topic.id)}
              swatchHex={resolvedTopicColor(themeConfig, topics, topic.id)}
              hasKids={kids.length > 0}
              isCollapsed={isCollapsed}
              guard={guard}
              reorderProps={reorderProps}
              onToggleCollapse={() => toggleCollapse(topic.id)}
              onToggleTopic={() => toggle(topic.id)}
            >
              {kids.length > 0 && !isCollapsed
                ? renderLevel(kids, depth + 1, topic.id)
                : null}
            </TopicItem>
          );
        })}
      </Reorder.Group>
    );
  }

  return (
    <SidebarSection
      title={t("common.topics")}
      manageTo="/settings/organize"
      grip={grip}
      onTitlePointerDown={onTitlePointerDown}
      collapsed={sectionCollapsed}
      onToggle={() => void setSidebarCollapsed("topics", !sectionCollapsed)}
    >
      <div className="mb-1 flex items-center gap-2 px-2">
        <ModeToggle mode={mode} setMode={setMode} noun={t("sidebar.topic")} />
        {selected.length > 0 && (
          <button type="button" onClick={clear} className="text-xs text-muted-foreground hover:text-foreground">{t("sidebar.clear")}</button>
        )}
      </div>
      {renderLevel(childrenOf.get(null) ?? [], 0, null)}
    </SidebarSection>
  );
}

function TopicItem({
  topic,
  depth,
  active,
  swatchHex,
  hasKids,
  isCollapsed,
  guard,
  reorderProps,
  onToggleCollapse,
  onToggleTopic,
  children,
}: {
  topic: Topic;
  depth: number;
  active: boolean;
  swatchHex: string | null;
  hasKids: boolean;
  isCollapsed: boolean;
  guard: ReturnType<typeof useDragClickGuard>;
  reorderProps: ReturnType<typeof useSidebarReorderProps>;
  onToggleCollapse: () => void;
  onToggleTopic: () => void;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const controls = useDragControls();
  return (
    <Reorder.Item
      as="div"
      value={topic.id}
      dragListener={false}
      dragControls={controls}
      onDragStart={guard.onDragStart}
      onDragEnd={guard.onDragEnd}
      // position:relative + base zIndex:0 ensure whileDrag elevation works
      // correctly when dragging upward past items above.
      style={{ position: "relative", zIndex: 0 }}
      whileDrag={{ zIndex: 10 }}
      {...reorderProps}
    >
      <div
        className={`flex items-center gap-1.5 rounded pr-2 text-sm hover:bg-accent ${active ? "bg-accent" : ""}`}
        style={{ paddingLeft: 8 + depth * 14 }}
      >
        {hasKids ? (
          <button
            type="button"
            onClick={onToggleCollapse}
            className="shrink-0 text-muted-foreground"
            aria-label={t(isCollapsed ? "sidebar.expand" : "sidebar.collapse", { title: topic.name })}
          >
            <ChevronRight className={`h-3 w-3 transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
          </button>
        ) : (
          <span className="w-3 shrink-0" />
        )}
        <button
          type="button"
          onClick={() => { if (guard.suppressClick()) return; onToggleTopic(); }}
          aria-pressed={active}
          className={`flex flex-1 items-center gap-1.5 py-1 text-left ${active ? "font-medium" : ""}`}
          title={active ? t("sidebar.filteringTopic") : t("sidebar.filterByTopic")}
        >
          <span className="h-2.5 w-2.5 shrink-0 rounded-full border" style={{ background: swatchHex ?? "transparent" }} />
          <span
            title={t("sidebar.dragTopic")}
            onPointerDown={(e) => controls.start(e)}
            className="min-w-0 truncate cursor-grab touch-none active:cursor-grabbing"
          >
            {topic.name}
          </span>
          <span aria-hidden className="min-w-0 flex-1" />
        </button>
      </div>
      {children}
    </Reorder.Item>
  );
}
